// OAuth consent (#272): the authorization server sends the browser to
// `/connect?request=<id>`. The id is the handle on a pending, not-yet-bound
// consent, so — like the live demo's `?live=<sid>` (utils/liveDemoSid.ts) — it
// must never reach analytics: config/posthog.ts takes it out of the address
// bar before PostHog records its first URL, parking it in sessionStorage
// (`basiqr.oauthRequest`, where the consent page and the login detour already
// look for it), and replay network URLs are scrubbed with
// `withoutOAuthRequestId`.
import {
  CONNECT_PATH,
  clearPendingOAuthRequest,
  getPendingOAuthRequest,
  isValidOAuthRequestId,
  setPendingOAuthRequest,
} from '../services/oauthService';

// Strip `request` from the address bar on /connect, keeping react-router's
// history state key, and park a valid id for the consent page. A malformed id
// is stripped and not kept (and supersedes any older parked one). A valid id
// that can't be parked (no sessionStorage) stays in the URL: a working consent
// beats a clean analytics URL.
export const captureOAuthRequestParam = (): void => {
  if (typeof window === 'undefined') return;
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return;
  }
  if (url.pathname !== CONNECT_PATH || !url.searchParams.has('request')) return;

  const id = url.searchParams.get('request') ?? '';
  if (isValidOAuthRequestId(id)) {
    setPendingOAuthRequest(id);
    if (getPendingOAuthRequest() !== id) return;
  } else {
    clearPendingOAuthRequest();
  }

  url.searchParams.delete('request');
  try {
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch (e) {
    // Cosmetic for the flow itself: the id is parked either way.
    console.warn('Failed to strip the request param from the URL:', e);
  }
};

// Absolute (`https://host/connect`) or relative (`/connect`) consent page path.
const CONNECT_PAGE = /^(?:[a-z][a-z0-9+.-]*:\/\/[^/?#]*)?\/connect\/?$/i;

// `url` without the pending request id, everything else untouched. For URLs
// that analytics records outside the address bar (replay network entries):
//  - the page's navigation entry keeps the URL it opened with,
//    `/connect?request=<id>` → `/connect`;
//  - the consent API calls carry it as a path segment,
//    `/api/oauth/requests/<id>[/decision]` → `/api/oauth/requests/:id[/decision]`.
export const withoutOAuthRequestId = (url: string): string => {
  const out = url.replace(/(\/oauth\/requests\/)[^/?#]+/g, '$1:id');

  const hashAt = out.indexOf('#');
  const beforeHash = hashAt === -1 ? out : out.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : out.slice(hashAt);
  const queryAt = beforeHash.indexOf('?');
  if (queryAt === -1) return out;
  const path = beforeHash.slice(0, queryAt);
  if (!CONNECT_PAGE.test(path)) return out;

  // Same shape as withoutLiveParam: drop `request=…`, then a dangling `?`/`&`.
  const query = beforeHash
    .slice(queryAt)
    .replace(/([?&])request=[^&]*&?/g, '$1')
    .replace(/[?&]$/, '');
  return `${path}${query}${hash}`;
};
